import { useState } from 'react';
import AppShell from '@/components/feature/AppShell';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MetricCard from '@/pages/dashboard/admin/components/MetricCard';
import SubjectProgressChart from '@/pages/dashboard/teacher-dashboard/components/SubjectProgressChart';
import AnnouncementsSection from '@/pages/dashboard/admin/components/AnnouncementsSection';
import CreateAnnouncementModal from '@/pages/dashboard/admin/components/CreateAnnouncementModal';
import StudentGrid from '@/pages/dashboard/teacher-dashboard/components/StudentGrid';
import CheckInWidget from '@/components/feature/CheckInWidget';
import { useToast } from '@/components/base/Toast';
import { useCheckIn } from '@/hooks/useCheckIn';
import { teacherMetricCards, teacherNotifications } from '@/mocks/dashboard';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { teacherLessonPlans } from '@/mocks/lessonPlanList';
import { announcements as initialAnnouncements, type Announcement } from '@/mocks/announcements';

const teacherMetricLabelKeys: Record<string, string> = {
  students: 'totalStudents',
  lessons: 'totalLessons',
  attendance: 'avgAttendance',
  pending: 'pendingTasks',
};

export default function TeacherDashboard() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const { isCheckedOut } = useCheckIn(currentUser.name);
  const toast = useToast();
  const now = new Date();
  const hour = now.getHours();
  const greeting =
    hour < 12 ? t('common.goodMorning') : hour < 17 ? t('common.goodAfternoon') : t('common.goodEvening');

  // Announcements state
  const [announcementsList, setAnnouncementsList] = useState<Announcement[]>([...initialAnnouncements]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleCreateAnnouncement = (data: {
    message: string;
    audioBlob?: Blob;
    facultyIds: string[] | 'all';
    parentIds: string[] | 'all';
    scheduleAt?: string;
  }) => {
    const newAnnouncement: Announcement = {
      id: `ann-${Date.now()}`,
      message: data.message,
      audioUrl: data.audioBlob ? URL.createObjectURL(data.audioBlob) : undefined,
      sentToFaculty: data.facultyIds,
      sentToParents: data.parentIds,
      sentBy: { name: currentUser.name, role: currentUser.role, avatar: currentUser.initials },
      createdAt: new Date().toISOString(),
      publishedAt: new Date().toISOString(),
      scheduledFor: data.scheduleAt,
      status: data.scheduleAt ? 'scheduled' : 'published',
      readBy: [currentUser.name],
    };

    setAnnouncementsList((prev) => [newAnnouncement, ...prev]);
    setShowCreateModal(false);
    toast.success(
      data.scheduleAt ? t('dashboard.announcementScheduled') : t('dashboard.announcementCreated')
    );
  };

  const handleDeleteAnnouncement = (id: string) => {
    setAnnouncementsList((prev) => prev.filter((a) => a.id !== id));
    toast.success(t('dashboard.announcementDeleted'));
  };

  // Compute lesson plan stats from live mock data
  const nonTemplatePlans = teacherLessonPlans.filter((p) => !p.isTemplate);
  const totalPlans = nonTemplatePlans.length;
  const submittedCount = nonTemplatePlans.filter((p) => p.status === 'submitted').length;
  const inProgressCount = nonTemplatePlans.filter((p) => p.status === 'in_progress').length;
  const completedCount = nonTemplatePlans.filter((p) => p.status === 'completed').length;
  const draftCount = nonTemplatePlans.filter((p) => p.status === 'draft').length;

  const lessonPlanChangeText = [
    submittedCount > 0 && `${submittedCount} submitted`,
    inProgressCount > 0 && `${inProgressCount} in progress`,
    completedCount > 0 && `${completedCount} completed`,
    draftCount > 0 && `${draftCount} drafts`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <AppShell
      pageTitle={t('dashboard.teacherDashboard')}
      pageSubtitle={t('dashboard.teacherSubtitle')}
      breadcrumbs={[{ label: t('common.home') }, { label: t('common.dashboard') }]}
      notifications={teacherNotifications}
    >
      {/* Greeting Banner */}
      <div className="gradient-hero rounded-2xl p-4 sm:p-6 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, oklch(0.72 0.17 58), transparent 70%)', transform: 'translate(30%, -30%)' }}
        />
        <div className="absolute bottom-0 left-1/3 w-32 h-32 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, oklch(0.60 0.12 215), transparent 70%)', transform: 'translateY(40%)' }}
        />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-foreground-300 text-xs sm:text-sm font-label font-medium mb-1">
              {now.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <h2 className="font-heading font-700 text-foreground-50 text-xl sm:text-2xl">
              {greeting}, {currentUser.name.split(' ')[0]}! 👋
            </h2>
            {isCheckedOut && (
              <p className="text-semantic-success text-xs font-label font-medium mt-1.5 flex items-center gap-1">
                <i className="ri-check-double-line" />
                {t('dashboard.attendanceRecorded')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              onClick={() => navigate('/dashboard/teacher-dashboard/lesson-plan')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex-1 sm:flex-none"
            >
              <i className="ri-add-line" />
              {t('dashboard.newLessonPlan')}
            </button>
            <button
              onClick={() => navigate('/dashboard/teacher-dashboard/attendance')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-foreground-50/10 border border-foreground-50/20 text-foreground-100 text-sm font-label font-medium rounded-lg hover:bg-foreground-50/20 transition-colors cursor-pointer whitespace-nowrap flex-1 sm:flex-none"
            >
              <i className="ri-file-chart-2-line" />
              {t('dashboard.markAttendance')}
            </button>
          </div>
        </div>
      </div>

      {/* Check-In / Check-Out Widget */}
      <CheckInWidget staffName={currentUser.name} />

      {/* Dashboard content */}
      <div>
        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {teacherMetricCards.map((metric) => (
            <MetricCard
              key={metric.id}
              label={t(`dashboard.${teacherMetricLabelKeys[metric.id] || metric.label}`, { defaultValue: metric.label })}
              value={metric.id === 'lessons' ? String(totalPlans) : metric.value}
              change={metric.id === 'lessons' ? lessonPlanChangeText : metric.change}
              trend={metric.trend as 'up' | 'down' | 'warning' | 'neutral'}
              icon={metric.icon}
              iconBg={metric.iconBg}
              iconColor={metric.iconColor}
              accent={metric.accent}
              onClick={
                metric.id === 'students'
                  ? () => navigate('/dashboard/teacher-dashboard/students')
                  : metric.id === 'lessons'
                    ? () => navigate('/dashboard/teacher-dashboard/lesson-plans')
                    : metric.id === 'attendance'
                      ? () => navigate('/dashboard/teacher-dashboard/attendance')
                      : undefined
              }
            />
          ))}
        </div>

        {/* Student Grid — below metric cards, above subject progress */}
        <div className="mb-6">
          <StudentGrid />
        </div>

        {/* Subject Progress — full width */}
        <div className="mb-6">
          <SubjectProgressChart />
        </div>

        {/* Announcements Section */}
        <div className="mb-6">
          <AnnouncementsSection
            announcements={announcementsList}
            currentUser={currentUser.name}
            onCreateClick={() => setShowCreateModal(true)}
            onDelete={handleDeleteAnnouncement}
          />
        </div>

        {/* Create Announcement Modal */}
        <CreateAnnouncementModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onPublish={handleCreateAnnouncement}
        />
      </div>
    </AppShell>
  );
}